import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '../auth';

// Cache global de rotas habilitadas por org, compartilhado entre remounts da
// Sidebar dentro do mesmo bundle JS. Só guarda last-known-good — nunca um
// fallback. Em erro/vazio, o cache é apagado e o estado local do consumer
// preserva o último valor bom (ver lógica abaixo).
type CacheEntry = {
  data: string[];
  timestamp: number;
};

const pagesCache = new Map<string, CacheEntry>();
const inflightRequests = new Map<string, Promise<string[] | null>>();

// Cache válido por 5 minutos
const CACHE_DURATION = 5 * 60 * 1000;
// Cooldown após erro/vazio antes de retentar automaticamente
const ERROR_COOLDOWN_MS = 30 * 1000;
const lastErrorAt = new Map<string, number>();

// Rotas mínimas para o primeiro paint (antes de qualquer fetch ter resolvido).
// IMPORTANTE: este conjunto NÃO é mais usado como fallback em erro/vazio —
// regredir para um subconjunto reduzido fazia páginas habilitadas (ex. /sequences,
// /chat, /email, /segments, /automations) sumirem da sidebar de MH GROUP quando
// a RPC falhava transitoriamente. Hoje DEFAULT só é o estado inicial; em
// falha, preservamos o last-known-good.
const DEFAULT_ENABLED_ROUTES = [
  '/dashboard',
  '/leads',
  '/companies',
  '/deals',
  '/activities',
  '/metas',
  '/metricas',
];

async function fetchOrgRoutes(orgId: string): Promise<string[] | null> {
  // Tentar RPC primeiro (mais rápido)
  const { data: rpcData, error: rpcError } = await supabase
    .rpc('get_organization_pages' as any, { org_id: orgId });

  if (!rpcError && rpcData && Array.isArray(rpcData)) {
    const routes = rpcData
      .filter((page: any) => page.is_enabled)
      .map((page: any) => page.route as string);
    return routes;
  }

  // Fallback: query direta
  const { data: directData, error: directError } = await supabase
    .from('organization_pages' as any)
    .select('pages!inner(route), is_enabled')
    .eq('org_id', orgId)
    .eq('is_enabled', true);

  if (directError) {
    throw directError;
  }

  if (directData && directData.length > 0) {
    return directData.map((item: any) => item.pages.route as string);
  }

  // Resposta vazia: devolve null para que o caller decida preservar o
  // last-known-good em vez de sobrescrever com [].
  return null;
}

export function useOrganizationPagesOptimized() {
  const [enabledRoutes, setEnabledRoutes] = useState<string[]>(DEFAULT_ENABLED_ROUTES);
  const [loading, setLoading] = useState(true);
  const orgId = useOrgId();
  const hasLoadedOnceRef = useRef(false);

  const loadEnabledRoutes = useCallback(async (force = false) => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    const now = Date.now();

    // Cache válido: usa direto.
    const cached = pagesCache.get(orgId);
    if (!force && cached && (now - cached.timestamp) < CACHE_DURATION) {
      setEnabledRoutes(cached.data);
      hasLoadedOnceRef.current = true;
      setLoading(false);
      return;
    }

    // Cooldown pós-erro: evita martelar a RPC quando ela está falhando.
    const lastErr = lastErrorAt.get(orgId);
    if (!force && lastErr && (now - lastErr) < ERROR_COOLDOWN_MS) {
      // Mantém o que estiver no state (last-known-good ou DEFAULT inicial).
      setLoading(false);
      return;
    }

    // Coalescer requests concorrentes para o mesmo orgId.
    let request = inflightRequests.get(orgId);
    if (!request) {
      request = fetchOrgRoutes(orgId)
        .catch((err) => {
          console.error('[useOrganizationPages] fetch falhou:', err);
          return null;
        })
        .finally(() => {
          inflightRequests.delete(orgId);
        });
      inflightRequests.set(orgId, request);
    }

    const routes = await request;

    if (routes && routes.length > 0) {
      pagesCache.set(orgId, { data: routes, timestamp: Date.now() });
      lastErrorAt.delete(orgId);
      setEnabledRoutes(routes);
      hasLoadedOnceRef.current = true;
    } else {
      // Vazio ou erro: NÃO regredir para DEFAULT. Preservar o conjunto atual
      // (que é DEFAULT no bootstrap, ou last-known-good após o primeiro fetch
      // bem-sucedido). Registrar cooldown para retentar mais tarde.
      console.warn(
        '[useOrganizationPages] resposta vazia ou erro para org', orgId,
        '— preservando últimas rotas conhecidas'
      );
      lastErrorAt.set(orgId, Date.now());
    }

    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    loadEnabledRoutes();
  }, [loadEnabledRoutes]);

  // Realtime: invalidate cache and refetch when organization_pages changes for this org
  useEffect(() => {
    if (!orgId) return;

    const channel = supabase
      .channel(`org-pages-${orgId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'organization_pages',
          filter: `org_id=eq.${orgId}`,
        },
        () => {
          pagesCache.delete(orgId);
          lastErrorAt.delete(orgId);
          loadEnabledRoutes(true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgId, loadEnabledRoutes]);

  const isRouteEnabled = useCallback((route: string) => {
    // Admin sempre habilitado
    if (route === '/admin') return true;
    // Settings sempre habilitado (configurações básicas)
    if (route === '/settings') return true;

    // Durante o bootstrap (antes do primeiro fetch bem-sucedido), só liberar
    // rotas que estão no conjunto atual — evita mostrar páginas que a org
    // possa não ter habilitado.
    return enabledRoutes.includes(route);
  }, [enabledRoutes]);

  const refetch = useCallback(() => {
    if (orgId) {
      pagesCache.delete(orgId);
      lastErrorAt.delete(orgId);
      loadEnabledRoutes(true);
    }
  }, [orgId, loadEnabledRoutes]);

  return {
    enabledRoutes,
    loading,
    isRouteEnabled,
    refetch,
  };
}
